// Third-party
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import camelCase from 'lodash/camelCase';
import ZendeskUtils from '@/utils/3rd-party/zendesk';

// Components
import { CardButton } from '@/views/components/buttons';
import { Checkbox, SearchInput } from '@/views/components/inputs';
import { withErrorBoundary } from '@/views/components/errors';
import ErrorTooltip from '@/views/components/tooltips/ErrorTooltip';
import FullPageModal from '@/views/components/modals/FullPageModal';
import Modal from '@/views/components/modals/AccessibleModal';
import ProposalEditorOptionsHeader from '@/views/components/modals/proposal-editor-options/ProposalEditorOptionsHeader';

// Hooks
import useScreenSize from '@/hooks/useScreenSize';

// Types
import { ReduxState } from '@/redux/modules/types';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { isDevDebugMode } from '@/utils/environment';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';

type StateProps = {
  user: Nullable<User>;
};

type Props = {
  show: boolean;
  templates: ProposalNS.BaseProposalTemplate[];
  templatesWithMissingRequirements?: { templateKey: string; missingRequirements: ProposalNS.FormattedProposalTemplateRequirements }[];
  onCancel: () => void;
  onSelect: (template: string, setDefault?: boolean) => void;
} & StateProps;

type SearchProps = {
  searchQuery: string;
  onSearchInputChange: (query: string) => void;
};

const Search: React.FC<SearchProps> = ({ searchQuery, onSearchInputChange }) => {
  if (!isDevDebugMode()) {
    return null;
  }

  return (
    <div className="mx-4 mb-3">
      <SearchInput placeholder="Search for templates" onChange={onSearchInputChange} value={searchQuery} className="w-100" />
    </div>
  );
};

const SelectProposalTemplateModal = (props: Props) => {
  const { onCancel, show, templates, user } = props;
  const [setAsDefault, setSetAsDefault] = useState<boolean>(false);
  const { isMobile, isTablet } = useScreenSize();
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (show) {
      ZendeskUtils.hideWidget();
    }
    return () => {
      if (userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
        ZendeskUtils.showWidget();
      }
    };
  }, [show, user]);

  const getMissingRequirementsList = (missingRequirements: ProposalNS.FormattedProposalTemplateRequirements) => missingRequirements.map(req => req.text);

  const renderTemplate = (template: ProposalNS.BaseProposalTemplate) => {
    const { templatesWithMissingRequirements, onSelect } = props;

    if (isDevDebugMode()) {
      const compare = [template.key.toLowerCase(), formatLocalizedString(template.title).toLowerCase()];
      if (!compare.some(field => field.includes(searchQuery))) {
        return null;
      }
    }

    const logoUrl = template.previewLogoUrl ? formatLocalizedString(template.previewLogoUrl) : undefined;
    const templateMissingRequirement = templatesWithMissingRequirements && templatesWithMissingRequirements.find(t => t.templateKey === template.key);

    return (
      <div key={template.key} className={templateMissingRequirement ? 'unselectable-template' : ''}>
        {templateMissingRequirement && (
          <ErrorTooltip
            list={getMissingRequirementsList(templateMissingRequirement.missingRequirements)}
            message={formatMessage('ProposalRequirements.Message')}
            title={formatMessage('ProposalRequirements.Title')}
          />
        )}
        <CardButton
          iconUrl={logoUrl}
          key={template.key}
          subtitle={template.description ? formatLocalizedString(template.description) : ''}
          title={template.title ? formatLocalizedString(template.title) : ''}
          onClick={templateMissingRequirement ? () => {} : () => onSelect(template.key, setAsDefault)}
          data-testid={`selectProposalTemplateModal-cardButton-${camelCase(template.key)}`}
        />
      </div>
    );
  };

  // Development mode only search functionality for templates
  const onSearchInputChange = (query: string) => {
    if (!isDevDebugMode()) {
      return;
    }

    setSearchQuery(query.toLowerCase());
  };

  const onSetAsDefault = (setAsDefault: boolean) => {
    setSetAsDefault(setAsDefault);
  };

  const TemplateGrid = () => (
    <div className="templates-container">
      <div className="templates-grid">{templates.map(renderTemplate)}</div>
    </div>
  );

  const DefaultCheckbox = () => (
    <Checkbox className="set-default" label={formatMessage('Proposal.Templates.SetAsDefault')} selected={setAsDefault} onChange={onSetAsDefault} />
  );

  return isMobile || isTablet ? (
    <FullPageModal show={show} className="select-proposal-template-modal" onExited={onCancel}>
      <ProposalEditorOptionsHeader title="Proposal.Tabs.Options.ReselectTemplate" onExit={onCancel} />
      <Modal.Body className="proposal-editor-options-body">
        <Search searchQuery={searchQuery} onSearchInputChange={onSearchInputChange} />
        <TemplateGrid />
      </Modal.Body>

      <Modal.Footer className="proposal-editor-options-footer">
        <DefaultCheckbox />
      </Modal.Footer>
    </FullPageModal>
  ) : (
    <Modal show={show} className="select-proposal-template-modal" onHide={onCancel} dialogClassName="modal-dialog-centered">
      <Modal.Body>
        <Search searchQuery={searchQuery} onSearchInputChange={onSearchInputChange} />
        <TemplateGrid />
        <DefaultCheckbox />
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user || null,
});

export default connect(mapStateToProps)(withErrorBoundary(SelectProposalTemplateModal));
